import {
  FileUpIcon,
  GaugeIcon,
  KeyRoundIcon,
  PlugZapIcon,
  XIcon,
  PackagePlusIcon,
  RefreshCwIcon,
  TriangleAlertIcon,
  UserIcon,
} from 'lucide-react'
import { useState } from 'react'

import { errorMessage } from '@/api/client'
import {
  useAccount,
  useAddons,
  useCloseAccount,
  useSetSecretLifetime,
  useSetPresenceTimeout,
  useSetBlockedMimeTypes,
  useSetUploadLimit,
  useChannels,
  useConnections,
  useMe,
  usePlans,
  useRotateToken,
  useSetAddon,
  useUsers,
} from '@/api/queries'
import { isMimePattern, normaliseMimePattern } from '@/api/mime'
import type { AccountSummary, RotateTokenResponse } from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { LifecycleBadge, RoleBadge } from '@/components/badges'
import { LimitMeter } from '@/components/LimitMeter'
import { SecretTokenPanel } from '@/components/SecretTokenPanel'
import { ErrorState } from '@/components/states'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { seatMembers } from '@/lib/access'
import { formatAbsolute, formatBytes, maskToken } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useActiveZone } from '@/zone/useActiveZone'
import { useReadOnly } from '@/staff/hooks'

function RotateTokenControl() {
  const rotate = useRotateToken()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [revealed, setRevealed] = useState<RotateTokenResponse | undefined>(undefined)

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setConfirmOpen(true)
        }}
        disabled={rotate.isPending}
      >
        <RefreshCwIcon className={cn('size-3.5', rotate.isPending ? 'animate-spin' : undefined)} />
        {rotate.isPending ? 'Rotating…' : 'Rotate token'}
      </Button>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate your bearer token?</AlertDialogTitle>
            <AlertDialogDescription>
              The token identifies <em>you</em>, across every zone you can reach. The current one
              stops working the instant the new one is issued, so every MCP client still
              configured with it — every editor, every agent, on every machine, in every zone —
              starts getting 401s until you update it. The new token is shown once and cannot be
              retrieved afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={rotate.isPending}>Keep current token</AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive' }))}
              onClick={(event) => {
                event.preventDefault()
                rotate.mutate(undefined, {
                  onSuccess: (data) => {
                    setConfirmOpen(false)
                    setRevealed(data)
                  },
                })
              }}
              disabled={rotate.isPending}
            >
              {rotate.isPending ? 'Rotating…' : 'Rotate and show me the new token'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={revealed !== undefined}
        onOpenChange={(open) => {
          if (!open) setRevealed(undefined)
        }}
      >
        <DialogContent className="sm:max-w-2xl" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Your new bearer token</DialogTitle>
          </DialogHeader>
          {revealed === undefined ? null : <SecretTokenPanel token={revealed.token} />}
          <DialogFooter>
            <Button
              onClick={() => {
                setRevealed(undefined)
              }}
            >
              I have stored it safely
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {rotate.isError ? (
        <Alert variant="destructive" className="mt-4">
          <AlertTitle>Rotation failed</AlertTitle>
          <AlertDescription>{errorMessage(rotate.error)}</AlertDescription>
        </Alert>
      ) : null}
    </>
  )
}

/* -------------------------------------------------------------------------- */
/* Add-ons (§19)                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Every add-on in the catalogue, not just the enrolled ones — a console that
 * could only list what you already have could never offer you anything new.
 *
 * §19 makes policing a **paid** add-on and leaves the billing mechanics
 * undefined, so what this renders is the record: what is enabled, and since
 * when. Nothing charges from it yet, and the page says so rather than implying
 * a price the product cannot yet quote. Disabling keeps the row on the server —
 * it is the billing evidence — so "since when" survives a toggle off and on.
 */
function AddonsCard({ isAdmin }: { isAdmin: boolean }) {
  const addons = useAddons()
  const setAddon = useSetAddon()
  const [pending, setPending] = useState<string | undefined>(undefined)

  if (addons.isError) {
    return (
      <ErrorState
        error={addons.error}
        title="Could not load your add-ons"
        onRetry={() => {
          void addons.refetch()
        }}
      />
    )
  }

  const rows = addons.data ?? []

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PackagePlusIcon className="size-4 text-primary" />
          Add-ons
        </CardTitle>
        <CardDescription>
          Capabilities beyond the plan, enabled per account. Nothing is billed from this yet — it
          is the record of what was enabled and since when, so that when billing arrives the
          history is already true.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {addons.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No add-ons are available yet.</p>
        ) : (
          rows.map((addon) => (
            <div
              key={addon.code}
              className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-border bg-[var(--surface)] px-4 py-3.5"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{addon.name}</span>
                  <Badge variant="outline" className="font-mono text-[0.625rem]">
                    {addon.code}
                  </Badge>
                  <Badge variant="secondary" className="text-[0.625rem]">
                    paid add-on
                  </Badge>
                  {addon.enabled ? (
                    <Badge variant="success" className="text-[0.625rem]">
                      enabled
                    </Badge>
                  ) : null}
                </div>
                <p className="max-w-xl text-xs text-pretty text-muted-foreground">
                  {addon.description}
                </p>
                {addon.enabled && addon.enabled_at !== null ? (
                  <p className="font-mono text-xs text-muted-foreground tabular">
                    since {formatAbsolute(addon.enabled_at)}
                  </p>
                ) : null}
              </div>
              {isAdmin ? (
                <Button
                  variant={addon.enabled ? 'outline' : 'default'}
                  size="sm"
                  disabled={setAddon.isPending && pending === addon.code}
                  onClick={() => {
                    setPending(addon.code)
                    setAddon.mutate({ code: addon.code, enabled: !addon.enabled })
                  }}
                >
                  {setAddon.isPending && pending === addon.code
                    ? 'Working…'
                    : addon.enabled
                      ? 'Disable'
                      : 'Enable'}
                </Button>
              ) : (
                <span className="text-xs text-muted-foreground">ADMINs only</span>
              )}
            </div>
          ))
        )}

        {setAddon.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not change that add-on</AlertTitle>
            <AlertDescription>{errorMessage(setAddon.error)}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}

/** Presets for how long an unread agent secret lives (D30). The server bounds it to 60 s .. 24 h. */
const PRESENCE_TIMEOUTS: ReadonlyArray<{ seconds: number; label: string }> = [
  { seconds: 300, label: '5 minutes' },
  { seconds: 900, label: '15 minutes' },
  { seconds: 1800, label: '30 minutes' },
  { seconds: 3600, label: '1 hour' },
  { seconds: 7200, label: '2 hours' },
  { seconds: 14400, label: '4 hours' },
  { seconds: 86400, label: '24 hours' },
]

const SECRET_LIFETIMES: ReadonlyArray<{ seconds: number; label: string }> = [
  { seconds: 300, label: '5 minutes' },
  { seconds: 900, label: '15 minutes' },
  { seconds: 3600, label: '1 hour (default)' },
  { seconds: 14_400, label: '4 hours' },
  { seconds: 86_400, label: '24 hours' },
]

/**
 * How long a secret passed between agents waits to be read (D30, work item 4).
 *
 * Says what it means rather than what it is: an unread secret is DESTROYED when
 * this elapses, and lowering it applies to secrets already waiting.
 */
function SecretLifetimeCard({ isAdmin, seconds }: { isAdmin: boolean; seconds: number | undefined }) {
  const update = useSetSecretLifetime()
  if (!isAdmin) return null

  const known = SECRET_LIFETIMES.some((option) => option.seconds === seconds)
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRoundIcon className="size-4 text-primary" />
          Secrets between agents
        </CardTitle>
        <CardDescription>
          Agents can pass a secret to one other agent with <code className="font-mono">secret</code> (op send).
          It is encrypted for the zone, readable exactly once, and never shown here.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="secret-lifetime">Unread secrets are destroyed after</Label>
          <select
            id="secret-lifetime"
            value={seconds ?? 3600}
            disabled={update.isPending || seconds === undefined}
            onChange={(event) => {
              update.mutate(Number(event.target.value))
            }}
            className="flex h-9 w-full max-w-xs rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs disabled:opacity-50"
          >
            {SECRET_LIFETIMES.map((option) => (
              <option key={option.seconds} value={option.seconds}>
                {option.label}
              </option>
            ))}
            {!known && seconds !== undefined ? (
              <option value={seconds}>{seconds} seconds (set via the API)</option>
            ) : null}
          </select>
          <p className="text-xs text-muted-foreground">
            Lowering this applies to secrets already waiting. A destroyed secret must be sent again;
            the recipient is told it expired rather than that it never existed.
          </p>
        </div>
        {update.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not change the lifetime</AlertTitle>
            <AlertDescription>{errorMessage(update.error)}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}


function PresenceTimeoutCard({ isAdmin, seconds }: { isAdmin: boolean; seconds: number | undefined }) {
  const update = useSetPresenceTimeout()
  if (!isAdmin) return null

  const known = PRESENCE_TIMEOUTS.some((option) => option.seconds === seconds)
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PlugZapIcon className="size-4 text-primary" />
          Connection presence
        </CardTitle>
        <CardDescription>
          An agent stays connected as long as it keeps calling tools. After this much silence its
          connection is released and its name freed for reuse.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="presence-timeout">Disconnect an idle agent after</Label>
          <select
            id="presence-timeout"
            value={seconds ?? 1800}
            disabled={update.isPending || seconds === undefined}
            onChange={(event) => {
              update.mutate(Number(event.target.value))
            }}
            className="flex h-9 w-full max-w-xs rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs disabled:opacity-50"
          >
            {PRESENCE_TIMEOUTS.map((option) => (
              <option key={option.seconds} value={option.seconds}>
                {option.label}
              </option>
            ))}
            {!known && seconds !== undefined ? (
              <option value={seconds}>{seconds} seconds (set via the API)</option>
            ) : null}
          </select>
          <p className="text-xs text-muted-foreground">
            Any tool call resets the clock. Lowering this can disconnect agents that are simply
            quiet; a disconnected agent reconnects with <code className="font-mono">connect</code>.
          </p>
        </div>
        {update.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not change the timeout</AlertTitle>
            <AlertDescription>{errorMessage(update.error)}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}

const MIB = 1024 * 1024

/**
 * The account's maximum upload size (D31 decision 18): an override that can only
 * LOWER the plan's ceiling. Stated in MiB because that is how the plans are
 * stated; sent in bytes because that is what the server compares against.
 *
 * The form refuses a value above the plan before the round trip, and the server
 * refuses it again with 400 — a form is an affordance and UI is not a control.
 */
function UploadLimitCard({ isAdmin, account }: { isAdmin: boolean; account: AccountSummary | undefined }) {
  const update = useSetUploadLimit()
  const [draft, setDraft] = useState<string | undefined>(undefined)
  const [saved, setSaved] = useState(false)
  const [localError, setLocalError] = useState<string | undefined>(undefined)
  if (!isAdmin) return null

  const planMax = account?.plan_max_upload_bytes
  const effective = account?.max_upload_bytes
  const planMib = planMax === undefined ? undefined : planMax / MIB
  const value = draft ?? (effective === undefined ? '' : String(Math.round((effective / MIB) * 100) / 100))

  const submit = (bytes: number | null) => {
    setLocalError(undefined)
    setSaved(false)
    update.mutate(bytes, {
      onSuccess: () => {
        setDraft(undefined)
        setSaved(true)
      },
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileUpIcon className="size-4 text-primary" />
          Files between agents
        </CardTitle>
        <CardDescription>
          Agents can pass a file to other agents with <code className="font-mono">upload</code> (op create).
          The bytes never enter a conversation; they travel over HTTPS and are stored until the
          account&apos;s retention runs out.
          {account?.uploads_enabled === false
            ? ' File transfer is not enabled on this server (no storage bucket is configured); the limits below apply once it is.'
            : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <form
          className="space-y-1.5"
          onSubmit={(event) => {
            event.preventDefault()
            const mib = Number(value)
            if (!Number.isFinite(mib) || mib <= 0) {
              setLocalError('Enter a size in MiB greater than zero.')
              return
            }
            const bytes = Math.round(mib * MIB)
            if (planMax !== undefined && bytes > planMax) {
              setLocalError(
                `That is above the plan maximum of ${formatBytes(planMax)}. An account can lower its limit, never raise it.`,
              )
              return
            }
            submit(bytes)
          }}
        >
          <Label htmlFor="upload-limit">Maximum file size (MiB)</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id="upload-limit"
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              className="max-w-[10rem]"
              value={value}
              disabled={update.isPending || account === undefined}
              onChange={(event) => {
                setDraft(event.target.value)
                setSaved(false)
                setLocalError(undefined)
              }}
            />
            <Button type="submit" size="sm" disabled={update.isPending || account === undefined}>
              {update.isPending ? 'Saving…' : 'Save limit'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={update.isPending || account === undefined}
              onClick={() => submit(null)}
            >
              Use plan maximum
            </Button>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="upload-plan-ceiling">
            {planMib === undefined
              ? 'Loading the plan ceiling…'
              : `The plan allows up to ${formatBytes(planMax)} (${planMib} MiB) per file; you can only lower it.`}
          </p>
        </form>
        {localError !== undefined ? (
          <Alert variant="destructive">
            <AlertTitle>Not saved</AlertTitle>
            <AlertDescription>{localError}</AlertDescription>
          </Alert>
        ) : null}
        {update.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not change the limit</AlertTitle>
            <AlertDescription>{errorMessage(update.error)}</AlertDescription>
          </Alert>
        ) : null}
        {saved && !update.isError ? (
          <p className="text-xs text-muted-foreground">
            Limit saved: {formatBytes(effective)} per file.
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}

/**
 * Blocked MIME types (D31 decision 13). Matched by the server against the
 * DETECTED type of an upload, never the declared one, so this is a policy tool
 * rather than a security boundary — and the copy must not call it one.
 */
function BlockedMimeTypesCard({ isAdmin, account }: { isAdmin: boolean; account: AccountSummary | undefined }) {
  const update = useSetBlockedMimeTypes()
  const [draft, setDraft] = useState('')
  const [localError, setLocalError] = useState<string | undefined>(undefined)
  if (!isAdmin) return null

  const patterns = account?.blocked_mime_types ?? []

  const replace = (next: string[]) => {
    setLocalError(undefined)
    update.mutate(next, { onSuccess: () => setDraft('') })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileUpIcon className="size-4 text-primary" />
          Blocked file types
        </CardTitle>
        <CardDescription>
          Uploads whose real type — read from the file&apos;s bytes, not from what the sender
          declared — matches one of these patterns are refused. Empty means every type is allowed.
          This is a policy tool, not malware scanning: a valid archive is a valid archive whatever
          it holds.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {patterns.length === 0 ? (
          <p className="text-xs text-muted-foreground">No types are blocked.</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {patterns.map((pattern) => (
              <li key={pattern} data-testid="blocked-mime">
                <Badge variant="outline" className="gap-1 pr-1 font-mono">
                  {pattern}
                  <button
                    type="button"
                    className="rounded-sm p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
                    aria-label={`Remove ${pattern}`}
                    disabled={update.isPending}
                    onClick={() => replace(patterns.filter((existing) => existing !== pattern))}
                  >
                    <XIcon className="size-3" />
                  </button>
                </Badge>
              </li>
            ))}
          </ul>
        )}

        <form
          className="space-y-1.5"
          onSubmit={(event) => {
            event.preventDefault()
            if (!isMimePattern(draft)) {
              setLocalError('Enter a pattern of the form type/subtype or type/*, e.g. application/x-msdownload or image/*.')
              return
            }
            const next = normaliseMimePattern(draft)
            if (patterns.includes(next)) {
              setLocalError(`${next} is already blocked.`)
              return
            }
            replace([...patterns, next])
          }}
        >
          <Label htmlFor="blocked-mime-input">Block a type</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id="blocked-mime-input"
              className="max-w-xs font-mono"
              placeholder="application/x-msdownload or image/*"
              value={draft}
              disabled={update.isPending || account === undefined}
              onChange={(event) => {
                setDraft(event.target.value)
                setLocalError(undefined)
              }}
            />
            <Button type="submit" size="sm" disabled={update.isPending || account === undefined}>
              {update.isPending ? 'Saving…' : 'Add pattern'}
            </Button>
          </div>
        </form>
        {localError !== undefined ? (
          <Alert variant="destructive">
            <AlertTitle>Not added</AlertTitle>
            <AlertDescription>{localError}</AlertDescription>
          </Alert>
        ) : null}
        {update.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not change the blocked types</AlertTitle>
            <AlertDescription>{errorMessage(update.error)}</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}

/**
 * Closing the account. Irreversible, ADMIN-only, and deliberately the last thing
 * on the page.
 *
 * Two things the copy has to say plainly, because getting either wrong is what
 * makes this button frightening in the wrong way:
 *
 *  - **It cannot be undone.** `ARCHIVED` is terminal server-side — `activate()`
 *    throws — so this is not a soft toggle dressed up as a warning.
 *  - **It does not delete anything.** Closing and erasure are different requests
 *    with different consequences, and a user who reads "close" as "destroy" will
 *    either avoid a button they wanted or press one they did not.
 */
function CloseAccountCard({ isAdmin, closed }: { isAdmin: boolean; closed: boolean }) {
  const [confirming, setConfirming] = useState(false)
  const close = useCloseAccount()

  if (!isAdmin) {
    return null
  }

  if (closed) {
    return (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TriangleAlertIcon className="size-4 text-destructive" />
            Account closed
          </CardTitle>
          <CardDescription>
            This account is retired and cannot be reopened. Nothing was deleted — the history is
            still here, and erasure remains a separate request.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TriangleAlertIcon className="size-4 text-destructive" />
          Close this account
        </CardTitle>
        <CardDescription>
          Retires the account permanently. Every zone stops accepting connections and everyone
          currently connected is disconnected.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertTitle>Closing does not delete your data</AlertTitle>
          <AlertDescription>
            Your zones, conversations and history stay exactly where they are. Deleting data is a
            separate request, asked for explicitly and with its own confirmation.
          </AlertDescription>
        </Alert>

        {close.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not close the account</AlertTitle>
            <AlertDescription>{errorMessage(close.error)}</AlertDescription>
          </Alert>
        ) : null}

        <Button
          variant="destructive"
          onClick={() => setConfirming(true)}
          disabled={close.isPending}
        >
          Close account
        </Button>
      </CardContent>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close this account?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. The account is retired, every zone stops accepting
              connections, and everyone connected right now is disconnected.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <Alert>
            <AlertTitle>Nothing is deleted</AlertTitle>
            <AlertDescription>
              Your data stays. Closing ends the account; it does not erase anything.
            </AlertDescription>
          </Alert>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={close.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive' }))}
              disabled={close.isPending}
              onClick={(event) => {
                event.preventDefault()
                close.mutate(undefined, { onSuccess: () => setConfirming(false) })
              }}
            >
              {close.isPending ? 'Closing…' : 'Yes, close it'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}

export function AccountPage() {
  const me = useMe()
  const account = useAccount()
  const plans = usePlans()
  const readOnly = useReadOnly()
  const isAdmin = me.data?.role === 'ADMIN'
  // A BILLING member reads usage and members (BI5) but has no token and no zones.
  const billingOnly = me.data?.role === 'BILLING'
  const users = useUsers(isAdmin || billingOnly)
  const { zones, activeZone } = useActiveZone()
  const connections = useConnections(activeZone?.id)
  const channels = useChannels(activeZone?.id)

  const plan = account.data?.plan ?? me.data?.plan
  const limits = account.data?.limits
  // ADMIN-only, like the members meter: `zones` is the caller's reach, and for a
  // USER that is the zones they were granted rather than the account's usage.
  const activeZoneCount = zones.filter((zone) => zone.zone_state === 'ACTIVE').length
  const memberCount = users.data === undefined ? undefined : seatMembers(users.data)

  return (
    <>
      <PageHeader
        eyebrow="Account"
        title={account.data?.name ?? 'Account'}
        description="Your identity and credential, the plan this account is on, and the limits actually in force."
      />

      <PageBody>
        {account.isError ? (
          <ErrorState
            error={account.error}
            title="Could not load your account"
            onRetry={() => {
              void account.refetch()
            }}
          />
        ) : null}

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon className="size-4 text-primary" />
                You
              </CardTitle>
              <CardDescription>
                Accounts are multi-user. Your role decides which zones you can reach and what you
                may administer.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <dl className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <dt className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                    Name
                  </dt>
                  <dd className="text-sm text-foreground">
                    {me.data?.user.name ?? <Skeleton className="h-4 w-28" />}
                  </dd>
                </div>
                <div className="space-y-1.5">
                  <dt className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                    Email
                  </dt>
                  <dd className="font-mono text-sm text-muted-foreground">
                    {me.data?.user.email ?? <Skeleton className="h-4 w-40" />}
                  </dd>
                </div>
                <div className="space-y-1.5">
                  <dt className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                    Role
                  </dt>
                  <dd>{me.data === undefined ? <Skeleton className="h-5 w-16" /> : <RoleBadge role={me.data.role} />}</dd>
                </div>
                <div className="space-y-1.5">
                  <dt className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                    Account state
                  </dt>
                  <dd>
                    {account.data === undefined ? (
                      <Skeleton className="h-5 w-20" />
                    ) : (
                      <LifecycleBadge state={account.data.account_state} />
                    )}
                  </dd>
                </div>
                <div className="space-y-1.5">
                  <dt className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                    Account slug
                  </dt>
                  <dd className="font-mono text-sm text-muted-foreground">
                    {account.data?.slug ?? <Skeleton className="h-4 w-24" />}
                  </dd>
                </div>
                <div className="space-y-1.5">
                  <dt className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                    Created
                  </dt>
                  <dd className="font-mono text-sm text-muted-foreground tabular">
                    {account.data === undefined ? (
                      <Skeleton className="h-4 w-36" />
                    ) : (
                      formatAbsolute(account.data.created_at)
                    )}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {billingOnly ? null : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRoundIcon className="size-4 text-primary" />
                Bearer token
              </CardTitle>
              <CardDescription>
                One token per user, valid in every zone you may reach — the URL decides the zone,
                the token decides who you are. Only the prefix is retrievable; saltseed stores a
                SHA-256 hash and nothing else.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="rounded-lg border border-border bg-[var(--surface)] px-3.5 py-3">
                <p className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                  In use
                </p>
                <p className="mt-1.5 font-mono text-sm text-foreground">
                  {me.data === undefined ? (
                    <Skeleton className="h-4 w-32" />
                  ) : (
                    maskToken(me.data.user.token_prefix)
                  )}
                </p>
              </div>
              <RotateTokenControl />
            </CardContent>
          </Card>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GaugeIcon className="size-4 text-primary" />
              Plan and effective limits
            </CardTitle>
            <CardDescription>
              The effective limit is the account override where one exists, and the plan&apos;s
              value otherwise. Quotas live in the database, so raising one is an update rather
              than a release.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              {plan === undefined ? (
                <Skeleton className="h-6 w-40" />
              ) : (
                <>
                  <Badge className="font-mono">{plan.code}</Badge>
                  <span className="text-sm text-foreground">{plan.name}</span>
                </>
              )}
            </div>

            <div className="grid gap-8 sm:grid-cols-2 xl:grid-cols-4">
              <LimitMeter
                label="Active zones"
                hint={isAdmin ? undefined : billingOnly ? 'Billing members have no zones.' : 'Visible to ADMINs only.'}
                used={isAdmin ? activeZoneCount : undefined}
                max={limits?.max_zones}
                planMax={plan?.max_zones}
                loading={account.isLoading}
              />
              <LimitMeter
                label={
                  activeZone === undefined
                    ? 'Connections per zone'
                    : `Connections in ${activeZone.name}`
                }
                used={connections.data?.filter((row) => row.state === 'CONNECTED').length}
                max={limits?.max_connections_per_zone}
                planMax={plan?.max_connections_per_zone}
                loading={account.isLoading || connections.isLoading}
              />
              <LimitMeter
                label={
                  activeZone === undefined ? 'Channels per zone' : `Channels in ${activeZone.name}`
                }
                used={channels.data?.length}
                max={limits?.max_channels_per_zone}
                planMax={plan?.max_channels_per_zone}
                loading={account.isLoading || channels.isLoading}
              />
              <LimitMeter
                label="Account members"
                hint={isAdmin || billingOnly ? undefined : 'Visible to ADMINs only.'}
                used={memberCount}
                max={limits?.max_members}
                planMax={plan?.max_members}
                loading={account.isLoading || ((isAdmin || billingOnly) && users.isLoading)}
              />
            </div>
          </CardContent>
        </Card>

        <AddonsCard isAdmin={isAdmin && !readOnly} />

        {plans.data === undefined || plans.data.length === 0 ? null : (
          <Card className="overflow-hidden">
            <CardHeader>
              <CardTitle>Plan catalogue</CardTitle>
              <CardDescription>
                What the other plans carry, for comparison. Changing plan is not something the
                console does yet.
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Plan</TableHead>
                    <TableHead className="text-right">Zones</TableHead>
                    <TableHead className="text-right">Connections / zone</TableHead>
                    <TableHead className="text-right">Channels / zone</TableHead>
                    <TableHead className="text-right">Members</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...plans.data]
                    .sort((left, right) => left.rank - right.rank)
                    .map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-foreground">{entry.code}</span>
                            {entry.code === plan?.code ? (
                              <Badge variant="outline" className="text-[0.625rem]">
                                current
                              </Badge>
                            ) : null}
                          </div>
                          <span className="mt-0.5 block text-xs text-muted-foreground">
                            {entry.name}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-muted-foreground tabular">
                          {entry.max_zones}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-muted-foreground tabular">
                          {entry.max_connections_per_zone}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-muted-foreground tabular">
                          {entry.max_channels_per_zone}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-muted-foreground tabular">
                          {entry.max_members}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
        {readOnly ? null : (
          <>
            <SecretLifetimeCard isAdmin={isAdmin} seconds={account.data?.secret_lifetime_seconds} />
            <PresenceTimeoutCard isAdmin={isAdmin} seconds={account.data?.presence_idle_seconds} />
            <UploadLimitCard isAdmin={isAdmin} account={account.data} />
            <BlockedMimeTypesCard isAdmin={isAdmin} account={account.data} />

            <CloseAccountCard
              isAdmin={isAdmin}
              closed={account.data?.account_state === 'ARCHIVED'}
            />
          </>
        )}
      </PageBody>
    </>
  )
}
